import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Mic, MicOff } from "lucide-react";
import { toast } from "sonner";

/**
 * One microphone button for every typing field in the app. Click into a text box and a small mic appears at its
 * edge; tap it, talk, and the words are typed into the box by the device's own speech-to-text (the browser's
 * speech recognition: the phone's or computer's dictation service). Tap again to stop.
 *
 * It attaches to whatever field has focus, so no screen needs changing. Passwords, emails, numbers, dates and
 * read-only fields never get one; add data-no-voice to a field to leave it out.
 */

type SR = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((e: any) => void) | null;
  onerror: ((e: any) => void) | null;
  onend: (() => void) | null;
};

const TEXT_TYPES = new Set(["text", "search", ""]);
type Field = HTMLInputElement | HTMLTextAreaElement;

function eligible(el: Element | null): el is Field {
  if (!el) return false;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled && !el.hasAttribute("data-no-voice");
  if (el instanceof HTMLInputElement) {
    return TEXT_TYPES.has((el.getAttribute("type") ?? "text").toLowerCase()) && !el.readOnly && !el.disabled && !el.hasAttribute("data-no-voice") && el.autocomplete !== "one-time-code";
  }
  return false;
}

function getRecognition(): (new () => SR) | null {
  if (typeof window === "undefined") return null;
  const w = window as any;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

/** Types text into a field the way a keyboard would, so React forms see the change. */
function insertText(el: Field, text: string) {
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
  if (!setter) return;
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? el.value.length;
  const before = el.value.slice(0, start);
  const after = el.value.slice(end);
  const spacer = before && !/\s$/.test(before) ? " " : "";
  const next = `${before}${spacer}${text}${after}`;
  setter.call(el, next);
  const caret = (before + spacer + text).length;
  try {
    el.setSelectionRange(caret, caret);
  } catch {
    // some input types do not support a caret
  }
  el.dispatchEvent(new Event("input", { bubbles: true }));
}

export function VoiceDictation() {
  const Recognition = getRecognition();
  const [target, setTarget] = useState<Field | null>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [listening, setListening] = useState(false);
  const recRef = useRef<SR | null>(null);
  const fieldRef = useRef<Field | null>(null);

  const stop = useCallback(() => {
    recRef.current?.stop();
  }, []);

  // Follow the focused field
  useEffect(() => {
    if (!Recognition) return;
    const onFocusIn = (e: FocusEvent) => {
      const t = e.target as Element | null;
      if (eligible(t)) setTarget(t);
      else if (!(t instanceof HTMLElement && t.closest("[data-voice-button]"))) {
        if (!recRef.current) setTarget(null);
      }
    };
    const onFocusOut = () => {
      // Keep the button while listening; otherwise drop it shortly after focus leaves (lets a tap on the button land)
      window.setTimeout(() => {
        if (recRef.current) return;
        if (!eligible(document.activeElement)) setTarget(null);
      }, 150);
    };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, [Recognition]);

  // Keep the button on the field while the page scrolls or resizes
  useEffect(() => {
    if (!target) {
      setRect(null);
      return;
    }
    const update = () => setRect(target.getBoundingClientRect());
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    const vv = window.visualViewport;
    vv?.addEventListener("resize", update);
    vv?.addEventListener("scroll", update);
    const id = window.setInterval(update, 500);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
      vv?.removeEventListener("resize", update);
      vv?.removeEventListener("scroll", update);
      window.clearInterval(id);
    };
  }, [target]);

  useEffect(() => () => recRef.current?.abort(), []);

  if (!Recognition || !target || !rect || !document.body.contains(target)) return null;

  const start = () => {
    const field = target;
    fieldRef.current = field;
    const rec = new Recognition();
    rec.lang = navigator.language || "en-CA";
    rec.continuous = true;
    rec.interimResults = false;
    rec.onresult = (e: any) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (!e.results[i].isFinal) continue;
        const text = String(e.results[i][0]?.transcript ?? "").trim();
        if (text && fieldRef.current) insertText(fieldRef.current, text);
      }
    };
    rec.onerror = (e: any) => {
      const code = e?.error;
      if (code === "not-allowed" || code === "service-not-allowed") toast.error("Microphone access is blocked. Allow it in your browser or phone settings.");
      else if (code === "no-speech") toast.message("Did not hear anything. Tap the mic and try again.");
      else if (code === "network") toast.error("Speech recognition needs an internet connection.");
      else if (code !== "aborted") toast.error("Could not use the microphone.");
    };
    rec.onend = () => {
      recRef.current = null;
      setListening(false);
    };
    recRef.current = rec;
    try {
      rec.start();
      setListening(true);
      field.focus();
    } catch {
      recRef.current = null;
      setListening(false);
    }
  };

  const isArea = target instanceof HTMLTextAreaElement;
  const size = 30;
  const left = Math.min(window.innerWidth - size - 4, rect.right - size - 6);
  const top = isArea ? rect.bottom - size - 6 : rect.top + (rect.height - size) / 2;
  // Off the screen (scrolled away): hide
  if (rect.bottom < 0 || rect.top > window.innerHeight) return null;

  return createPortal(
    <button
      type="button"
      data-voice-button
      aria-label={listening ? "Stop dictation" : "Speak to type"}
      title={listening ? "Stop" : "Speak to type"}
      // Keep the field focused while the button is pressed
      // (and keep a dialog around the field from treating the tap as "outside" and closing)
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => (listening ? stop() : start())}
      style={{ position: "fixed", left, top, width: size, height: size, zIndex: 70, pointerEvents: "auto" }}
      className={
        "flex items-center justify-center rounded-full border shadow-sm transition-colors " +
        (listening ? "animate-pulse border-red-600 bg-red-600 text-white" : "border-border bg-background/95 text-muted-foreground hover:text-foreground")
      }
    >
      {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
    </button>,
    document.body
  );
}
